import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./Table";
import { User, UserRole } from '@/types';
import { useToast } from '@/contexts/ToastContext';
import { API_BASE_URL } from '@/config';
import { useLogger } from '@/contexts/LogContext';
import { LogLevel, LogCategory } from '@/types/logs';
import { Loader2, Search } from 'lucide-react';
import { useWallet } from '@/contexts/WalletContext';
import { useArtists } from '@/hooks/data/useArtists';

const ActionButton: React.FC<{ onClick: () => void; className: string; children: React.ReactNode }> = ({ onClick, className, children }) => (
  <button
    onClick={onClick}
    className={`text-xs font-semibold px-2.5 py-1 rounded-md transition-colors ${className}`}>{children}</button>
);

const TableActions: React.FC<{ 
  artist: User;
  role: UserRole; 
  onAction: (action: string, artistId: string) => void;
}> = ({ artist, role, onAction }) => {
  const handleAction = (action: string) => onAction(action, artist.id);
  const isAdmin = role === UserRole.ADMIN || role === UserRole.SYSTEM_ADMIN_LIVE;
  
  if (isAdmin) {
    return (
        <div className="space-x-2">
          <ActionButton onClick={() => handleAction('edit')} className="bg-blue-500/20 text-blue-300 hover:bg-blue-500/40">Edit</ActionButton>
          <ActionButton onClick={() => handleAction('delete')} className="bg-red-500/20 text-red-300 hover:bg-red-500/40">Delete</ActionButton>
        </div>
      );
  }
  return <></>;
};

const ArtistRealDataTable = ({ currentUserRole, currentUserId }: { currentUserRole: UserRole, currentUserId: string }) => {
  const { artists: allArtists, isLoading, error, refetch } = useArtists();
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const { notify } = useToast();
  const { addLog } = useLogger();
  const { isConnected } = useWallet();
  const PAGE_SIZE = 10;

  const handleAction = async (action: string, artistId: string) => {
    if (action === 'delete') {
      if (!window.confirm('Are you sure you want to delete this artist?')) return;
      addLog(`Admin ${currentUserId} initiated deletion of artist ${artistId}`, LogLevel.INFO, LogCategory.ARTIST);
      try {
        const response = await fetch(`${API_BASE_URL}/api/users/${artistId}`, { method: 'DELETE', credentials: 'include' });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to delete artist via API.');
        }
        refetch(); // Refetch data after deletion
        notify(`Artist ${artistId} was successfully deleted.`, 'success');
        addLog(`Artist ${artistId} successfully deleted by ${currentUserId}.`, LogLevel.INFO, LogCategory.ARTIST);
      } catch (err) {
        notify((err as Error).message, 'error');
        addLog(`Error deleting artist ${artistId}: ${(err as Error).message}`, LogLevel.ERROR, LogCategory.ARTIST);
      }
    } else {
      notify(`Action '${action}' for artist '${artistId}' is not implemented.`, 'info');
    }
  };

  const filteredArtists = useMemo(() => {
    return allArtists.filter(a => 
      (a.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (a.artistProfile?.genre || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allArtists, searchTerm]);

  const displayedArtists = useMemo(() => {
      return filteredArtists.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  }, [filteredArtists, page]);

  const hasMore = page * PAGE_SIZE < filteredArtists.length;

  if (isLoading && allArtists.length === 0) return (
    <div className="flex justify-center items-center h-48">
      <Loader2 className="w-8 h-8 text-kala-secondary animate-spin" />
      <p className="ml-4 text-kala-400">Loading artists...</p>
    </div>
  );

  return (
    <div className="bg-kala-900 text-white rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Artists</h2>
        <div className="relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-kala-500" />
            <input
                type="text"
                placeholder="Search by name or genre..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="bg-kala-800 border border-kala-700 rounded-full pl-10 pr-4 py-2 text-sm text-white focus:ring-1 focus:ring-kala-secondary outline-none w-64"
            />
        </div>
      </div>
      {error && <div className="p-4 mb-4 bg-yellow-500/10 text-yellow-400 text-center text-sm rounded-lg">Warning: {error}</div>}
      {!isConnected && (
        <div className="p-2 mb-4 bg-kala-900/30 text-kala-500 text-center text-[10px] uppercase tracking-widest border-y border-kala-700">
          Note: Displaying fallback data. For real-time information, connect your wallet.
        </div>
      )}

      <Table>
        <TableCaption>A list of artists in the roster. {allArtists.length === 0 && !isLoading && "No artists found."}</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Genre</TableHead>
            <TableHead>Bio</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayedArtists.map((artist) => (
            <TableRow key={artist.id}>
              <TableCell>{artist.name || 'N/A'}</TableCell>
              <TableCell>{artist.artistProfile?.genre || 'N/A'}</TableCell>
              <TableCell className="max-w-xs truncate">{artist.artistProfile?.bio || 'N/A'}</TableCell>
              <TableCell className="text-right">
                <TableActions artist={artist} role={currentUserRole} onAction={handleAction} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

        {displayedArtists.length === 0 && !isLoading && (
            <div className="text-center p-8 text-kala-500">
                <p>No artists found for your search term.</p>
            </div>
        )}

      <div className="flex justify-between items-center mt-6">
        <button 
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page === 1 || isLoading}
          className="px-4 py-2 bg-kala-700 hover:bg-kala-600 text-white font-bold rounded-lg disabled:opacity-50 transition-colors"
        >
          Previous
        </button>
        <span className="text-kala-400">Page {page} of {Math.ceil(filteredArtists.length / PAGE_SIZE)}</span>
        <button 
          onClick={() => setPage(p => p + 1)}
          disabled={!hasMore || isLoading}
          className="px-4 py-2 bg-kala-700 hover:bg-kala-600 text-white font-bold rounded-lg disabled:opacity-50 transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default ArtistRealDataTable;
